import { riskBand, riskLabel } from "../../constants/risk";

export default function RecentScansTable({ scans = [], onViewScan }) {
  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const STATUS = {
    completed: { label: "Completed", dot: "bg-green-500", text: "text-green-700" },
    completed_with_errors: { label: "Failed", dot: "bg-red-500", text: "text-red-700" },
    in_progress: { label: "In progress", dot: "bg-brand-500", text: "text-brand-700" },
    processing: { label: "In progress", dot: "bg-brand-500", text: "text-brand-700" },
    failed: { label: "Failed", dot: "bg-red-500", text: "text-red-700" },
    cancelled: { label: "Cancelled", dot: "bg-input", text: "text-muted-foreground" },
    pending: { label: "Pending", dot: "bg-muted-foreground", text: "text-muted-foreground" },
    default: { label: "Pending", dot: "bg-muted-foreground", text: "text-muted-foreground" },
  };

  // Bands come from the shared scale (constants/risk.js) rather than being
  // restated here - three different sets of thresholds coexisted on this page,
  // with no legend defining any of them.
  //
  // `hasScore` is computed by the backend: results.riskScore defaults to 0, so
  // a scan that analysed nothing is indistinguishable from one that measured
  // clean, and 28 of 56 production scans carried such a 0. Those rendered as a
  // green "Clear". A scan with no score now shows a dash and says so on hover.
  const riskBadge = (scan) => {
    if (scan.hasScore === false || typeof scan.riskScore !== "number") {
      return { label: "-", cls: "text-muted-foreground", title: "No risk score: this scan analysed no posts" };
    }
    const band = riskBand(scan.riskScore);
    return { label: riskLabel(scan.riskScore), cls: band.badge, title: null };
  };

  if (scans.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-input bg-card p-10 text-center">
        <p className="text-sm font-medium text-foreground">No scans yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Your completed scans will show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
      <div className="grid grid-cols-[3fr,1.5fr,1.5fr,1.5fr] gap-2 px-5 py-3 bg-muted border-b border-border text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span>Client</span>
        <span>Date</span>
        <span>Status</span>
        <span className="text-right" title="What they post could cost them with sponsors and platforms">Exposure</span>
      </div>
      <div className="divide-y divide-border">
        {scans.map((scan) => {
          const st = STATUS[scan.status] || STATUS.default;
          const risk = riskBadge(scan);
          return (
            <div
              key={scan.id}
              className="grid grid-cols-[3fr,1.5fr,1.5fr,1.5fr] gap-2 items-center px-5 py-3 cursor-pointer hover:bg-muted transition-colors"
              onClick={() => onViewScan && onViewScan(scan.id)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={scan.client?.avatar || "https://placehold.co/32x32"}
                  alt={scan.client?.name}
                  className="w-8 h-8 rounded-full shrink-0 object-cover"
                />
                <div className="min-w-0">
                  <div className="font-medium text-foreground truncate">{scan.client?.name}</div>
                  <div className="text-sm text-muted-foreground truncate">{scan.client?.handle}</div>
                </div>
              </div>
              <span className="text-sm text-muted-foreground">{formatDate(scan.startedAt)}</span>
              <span className={`inline-flex items-center gap-1.5 text-sm ${st.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                {st.label}
              </span>
              <div className="flex justify-end">
                {risk.cls.startsWith("bg-") ? (
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${risk.cls}`}>
                    {risk.label}
                  </span>
                ) : (
                  <span className={`text-sm ${risk.cls}`} title={risk.title || undefined}>{risk.label}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
